import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Briefcase, Sparkle, Coin, Plus, PencilSimple, Trash, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

const emptyProfession = { code: '', label: '', active: true, order: 0 };
const emptyService = {
  name: '',
  profession_codes: [],
  credit_cost: 10,
  default_price_eur: 0,
  description: '',
  active: true,
};

const TaxonomyPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [professions, setProfessions] = useState([]);
  const [services, setServices] = useState([]);
  const [platformCfg, setPlatformCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const [profOpen, setProfOpen] = useState(false);
  const [profForm, setProfForm] = useState(emptyProfession);
  const [profEditingId, setProfEditingId] = useState(null);

  const [svcOpen, setSvcOpen] = useState(false);
  const [svcForm, setSvcForm] = useState(emptyService);
  const [svcEditingId, setSvcEditingId] = useState(null);

  const [cfgSaving, setCfgSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && user && user.role !== 'admin') {
      toast.error('Accesso riservato agli admin');
      navigate('/');
    }
    if (user?.role === 'admin') { fetchAll(); }
  }, [user, authLoading, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, c] = await Promise.all([
        api.get('/api/admin/professions'),
        api.get('/api/admin/services-catalog'),
        api.get('/api/admin/platform-config'),
      ]);
      setProfessions(p.data);
      setServices(s.data);
      setPlatformCfg(c.data);
    } catch (e) { toast.error('Errore caricamento tassonomia'); }
    setLoading(false);
  }, []);

  // ===== Professions =====
  const openProfCreate = () => {
    setProfEditingId(null);
    setProfForm({ ...emptyProfession, order: (professions.length + 1) * 10 });
    setProfOpen(true);
  };
  const openProfEdit = (p) => {
    setProfEditingId(p.profession_id);
    setProfForm({ code: p.code, label: p.label, active: p.active, order: p.order || 0 });
    setProfOpen(true);
  };
  const saveProf = async () => {
    if (!profForm.code.trim() || !profForm.label.trim()) {
      toast.error('Codice ed etichetta obbligatori');
      return;
    }
    try {
      if (profEditingId) {
        await api.put(`/api/admin/professions/${profEditingId}`, profForm);
        toast.success('Professione aggiornata');
      } else {
        await api.post('/api/admin/professions', profForm);
        toast.success('Professione creata');
      }
      setProfOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
  };
  const deleteProf = async (p) => {
    if (!window.confirm(`Disattivare "${p.label}"?`)) return;
    try {
      await api.delete(`/api/admin/professions/${p.profession_id}`);
      toast.success('Professione disattivata');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  // ===== Services =====
  const toggleProfCode = (code) => {
    setSvcForm((f) => ({
      ...f,
      profession_codes: f.profession_codes.includes(code)
        ? f.profession_codes.filter((c) => c !== code)
        : [...f.profession_codes, code],
    }));
  };
  const openSvcCreate = () => {
    setSvcEditingId(null);
    setSvcForm(emptyService);
    setSvcOpen(true);
  };
  const openSvcEdit = (s) => {
    setSvcEditingId(s.service_id);
    setSvcForm({
      name: s.name,
      profession_codes: s.profession_codes || [],
      credit_cost: s.credit_cost || 0,
      default_price_eur: s.default_price_eur || 0,
      description: s.description || '',
      active: s.active,
    });
    setSvcOpen(true);
  };
  const saveSvc = async () => {
    if (!svcForm.name.trim()) { toast.error('Nome prestazione obbligatorio'); return; }
    if (!svcForm.profession_codes.length) { toast.error('Seleziona almeno una professione'); return; }
    try {
      const payload = {
        ...svcForm,
        credit_cost: Number(svcForm.credit_cost) || 0,
        default_price_eur: Number(svcForm.default_price_eur) || 0,
      };
      if (svcEditingId) {
        await api.put(`/api/admin/services-catalog/${svcEditingId}`, payload);
        toast.success('Prestazione aggiornata');
      } else {
        await api.post('/api/admin/services-catalog', payload);
        toast.success('Prestazione creata');
      }
      setSvcOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
  };
  const deleteSvc = async (s) => {
    if (!window.confirm(`Eliminare la prestazione "${s.name}"?`)) return;
    try {
      await api.delete(`/api/admin/services-catalog/${s.service_id}`);
      toast.success('Prestazione eliminata');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  // ===== Platform config =====
  const updateCfgField = (path, value) => {
    setPlatformCfg((cfg) => ({ ...cfg, [path]: value }));
  };
  const updateCompletionCost = (key, value) => {
    setPlatformCfg((cfg) => ({
      ...cfg,
      credits_per_completion: { ...(cfg.credits_per_completion || {}), [key]: value },
    }));
  };
  const updatePackageField = (idx, key, value) => {
    setPlatformCfg((cfg) => {
      const list = [...(cfg.credit_recharge_packages || [])];
      list[idx] = { ...list[idx], [key]: key === 'code' || key === 'label' ? value : Number(value) };
      return { ...cfg, credit_recharge_packages: list };
    });
  };
  const updatePlanField = (idx, key, value) => {
    setPlatformCfg((cfg) => {
      const list = [...(cfg.subscription_plans || [])];
      list[idx] = {
        ...list[idx],
        [key]: (key === 'eur' || key === 'included_services_per_month') ? Number(value) : value,
      };
      return { ...cfg, subscription_plans: list };
    });
  };
  const saveCfg = async () => {
    setCfgSaving(true);
    try {
      const payload = {
        wallet_free_credits_on_signup: Number(platformCfg.wallet_free_credits_on_signup) || 0,
        credits_per_request_default: Number(platformCfg.credits_per_request_default) || 0,
        credits_per_completion: Object.fromEntries(
          Object.entries(platformCfg.credits_per_completion || {}).map(([k, v]) => [k, Number(v) || 0])
        ),
        wallet_low_threshold: Number(platformCfg.wallet_low_threshold) || 0,
        urgent_surcharge_min_eur: Number(platformCfg.urgent_surcharge_min_eur) || 0,
        urgent_surcharge_default_eur: Number(platformCfg.urgent_surcharge_default_eur) || 0,
        emergency_notice_112: platformCfg.emergency_notice_112 || '',
        qualified_personnel_notice: platformCfg.qualified_personnel_notice || '',
        credit_recharge_packages: platformCfg.credit_recharge_packages || [],
        subscription_plans: platformCfg.subscription_plans || [],
      };
      const r = await api.put('/api/admin/platform-config', payload);
      setPlatformCfg(r.data);
      toast.success('Config piattaforma salvata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
    setCfgSaving(false);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 py-12">Caricamento…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-12" data-testid="taxonomy-page">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <Button variant="ghost" size="sm" className="mb-2" onClick={() => navigate('/admin')} data-testid="back-to-admin-btn">
              <ArrowLeft size={16} className="mr-1" /> Torna a Admin
            </Button>
            <h1 className="font-heading font-semibold text-4xl text-foreground mb-2">Tassonomia & Config</h1>
            <p className="text-foreground/70">Professioni, Prestazioni master, Config piattaforma</p>
          </div>
        </div>

        <Tabs defaultValue="professions" className="w-full">
          <TabsList data-testid="taxonomy-tabs" className="flex flex-wrap gap-2">
            <TabsTrigger value="professions" data-testid="tab-professions">
              <Briefcase size={16} className="mr-2" /> Professioni ({professions.length})
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">
              <Sparkle size={16} className="mr-2" /> Prestazioni ({services.length})
            </TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config">
              <Coin size={16} className="mr-2" /> Config piattaforma
            </TabsTrigger>
          </TabsList>

          {/* PROFESSIONS */}
          <TabsContent value="professions" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button className="rounded-full" onClick={openProfCreate} data-testid="new-profession-btn">
                <Plus size={16} className="mr-2" /> Nuova professione
              </Button>
            </div>
            <Card className="neumorphism rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left">
                    <th className="p-3">Codice</th>
                    <th className="p-3">Etichetta</th>
                    <th className="p-3">Ordine</th>
                    <th className="p-3">Stato</th>
                    <th className="p-3 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {professions.map((p) => (
                    <tr key={p.profession_id} className="border-t border-border/50" data-testid={`prof-row-${p.code}`}>
                      <td className="p-3 font-mono text-xs">{p.code}</td>
                      <td className="p-3">{p.label}</td>
                      <td className="p-3">{p.order || 0}</td>
                      <td className="p-3">
                        <Badge className={p.active ? 'bg-sky-100 text-sky-800' : 'bg-gray-200 text-gray-600'}>
                          {p.active ? 'Attiva' : 'Disattiva'}
                        </Badge>
                      </td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openProfEdit(p)} data-testid={`edit-prof-${p.code}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteProf(p)} data-testid={`delete-prof-${p.code}`}>
                          <Trash size={14} className="text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          {/* SERVICES */}
          <TabsContent value="services" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button className="rounded-full" onClick={openSvcCreate} data-testid="new-service-btn">
                <Plus size={16} className="mr-2" /> Nuova prestazione
              </Button>
            </div>
            <Card className="neumorphism rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left">
                    <th className="p-3">Nome</th>
                    <th className="p-3">Professioni</th>
                    <th className="p-3">Crediti</th>
                    <th className="p-3">Prezzo €</th>
                    <th className="p-3">Stato</th>
                    <th className="p-3 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s) => (
                    <tr key={s.service_id} className="border-t border-border/50" data-testid={`svc-row-${s.service_id}`}>
                      <td className="p-3">{s.name}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(s.profession_codes || []).map((c) => (
                            <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="p-3">{s.credit_cost}</td>
                      <td className="p-3">€{Number(s.default_price_eur || 0).toFixed(2)}</td>
                      <td className="p-3">
                        <Badge className={s.active ? 'bg-sky-100 text-sky-800' : 'bg-gray-200 text-gray-600'}>
                          {s.active ? 'Attiva' : 'Disattiva'}
                        </Badge>
                      </td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openSvcEdit(s)} data-testid={`edit-svc-${s.service_id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteSvc(s)} data-testid={`delete-svc-${s.service_id}`}>
                          <Trash size={14} className="text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          {/* PLATFORM CONFIG */}
          <TabsContent value="config" className="mt-6">
            {platformCfg && (
              <div className="space-y-6">
                <Card className="neumorphism rounded-2xl p-6">
                  <h3 className="font-semibold text-lg mb-4">Wallet</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Crediti gratuiti alla registrazione</Label>
                      <Input
                        type="number"
                        value={platformCfg.wallet_free_credits_on_signup ?? 0}
                        onChange={(e) => updateCfgField('wallet_free_credits_on_signup', e.target.value)}
                        data-testid="cfg-free-credits"
                      />
                    </div>
                    <div>
                      <Label>Soglia alert wallet basso</Label>
                      <Input
                        type="number"
                        value={platformCfg.wallet_low_threshold ?? 10}
                        onChange={(e) => updateCfgField('wallet_low_threshold', e.target.value)}
                        data-testid="cfg-wallet-threshold"
                      />
                    </div>
                  </div>
                </Card>

                <Card className="neumorphism rounded-2xl p-6" data-testid="cfg-completion-costs">
                  <h3 className="font-semibold text-lg mb-1">Crediti per prestazione conclusa</h3>
                  <p className="text-sm text-foreground/60 mb-4">
                    Crediti scalati al professionista quando una prestazione viene completata, in base al suo piano.
                  </p>
                  <div className="grid md:grid-cols-5 gap-4">
                    {[
                      ['default', 'Senza Premium'],
                      ['pro_monthly', 'Premium 1 mese'],
                      ['pro_semiannual', 'Premium 6 mesi'],
                      ['pro_annual', 'Premium annuale'],
                      ['pro_annual_centro', 'Annuale Centro'],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <Label>{label}</Label>
                        <Input
                          type="number"
                          value={platformCfg.credits_per_completion?.[key] ?? 0}
                          onChange={(e) => updateCompletionCost(key, e.target.value)}
                          data-testid={`cfg-completion-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="neumorphism rounded-2xl p-6">
                  <h3 className="font-semibold text-lg mb-4">Pacchetti crediti</h3>
                  <div className="space-y-3">
                    {(platformCfg.credit_recharge_packages || []).map((pkg, idx) => (
                      <div key={idx} className="grid md:grid-cols-4 gap-3" data-testid={`pkg-row-${idx}`}>
                        <Input value={pkg.code} onChange={(e) => updatePackageField(idx, 'code', e.target.value)} placeholder="code" />
                        <Input type="number" value={pkg.eur} onChange={(e) => updatePackageField(idx, 'eur', e.target.value)} placeholder="€" />
                        <Input type="number" value={pkg.credits} onChange={(e) => updatePackageField(idx, 'credits', e.target.value)} placeholder="crediti" />
                        <Input value={pkg.label} onChange={(e) => updatePackageField(idx, 'label', e.target.value)} placeholder="label" />
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="neumorphism rounded-2xl p-6">
                  <h3 className="font-semibold text-lg mb-4">Piani abbonamento professionista</h3>
                  <div className="space-y-3">
                    {(platformCfg.subscription_plans || []).map((pl, idx) => (
                      <div key={idx} className="grid md:grid-cols-5 gap-3" data-testid={`plan-row-${idx}`}>
                        <Input value={pl.code} onChange={(e) => updatePlanField(idx, 'code', e.target.value)} placeholder="code" />
                        <Input type="number" value={pl.eur} onChange={(e) => updatePlanField(idx, 'eur', e.target.value)} placeholder="€" />
                        <Input value={pl.period} onChange={(e) => updatePlanField(idx, 'period', e.target.value)} placeholder="month|6month|year" />
                        <Input value={pl.label} onChange={(e) => updatePlanField(idx, 'label', e.target.value)} placeholder="label" />
                        <Input
                          type="number"
                          value={pl.included_services_per_month ?? 0}
                          onChange={(e) => updatePlanField(idx, 'included_services_per_month', e.target.value)}
                          placeholder="prestazioni/mese"
                          data-testid={`plan-included-${idx}`}
                        />
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="neumorphism rounded-2xl p-6">
                  <h3 className="font-semibold text-lg mb-4">Sovrapprezzo richiesta urgente</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Sovrapprezzo minimo €</Label>
                      <Input
                        type="number"
                        value={platformCfg.urgent_surcharge_min_eur ?? 0}
                        onChange={(e) => updateCfgField('urgent_surcharge_min_eur', e.target.value)}
                        data-testid="cfg-urgent-min"
                      />
                    </div>
                    <div>
                      <Label>Sovrapprezzo default €</Label>
                      <Input
                        type="number"
                        value={platformCfg.urgent_surcharge_default_eur ?? 0}
                        onChange={(e) => updateCfgField('urgent_surcharge_default_eur', e.target.value)}
                        data-testid="cfg-urgent-default"
                      />
                    </div>
                  </div>
                </Card>

                <Card className="neumorphism rounded-2xl p-6">
                  <h3 className="font-semibold text-lg mb-4">Avvisi</h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Avviso emergenza 112</Label>
                      <Textarea
                        rows={3}
                        value={platformCfg.emergency_notice_112 || ''}
                        onChange={(e) => updateCfgField('emergency_notice_112', e.target.value)}
                        data-testid="cfg-emergency-notice"
                      />
                    </div>
                    <div>
                      <Label>Avviso personale qualificato</Label>
                      <Textarea
                        rows={3}
                        value={platformCfg.qualified_personnel_notice || ''}
                        onChange={(e) => updateCfgField('qualified_personnel_notice', e.target.value)}
                        data-testid="cfg-qualified-notice"
                      />
                    </div>
                  </div>
                </Card>

                <div className="flex justify-end">
                  <Button className="rounded-full" onClick={saveCfg} disabled={cfgSaving} data-testid="save-config-btn">
                    {cfgSaving ? 'Salvataggio…' : 'Salva configurazione'}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Profession dialog */}
      <Dialog open={profOpen} onOpenChange={setProfOpen}>
        <DialogContent data-testid="profession-dialog">
          <DialogHeader>
            <DialogTitle>{profEditingId ? 'Modifica professione' : 'Nuova professione'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Codice</Label>
              <Input
                value={profForm.code}
                onChange={(e) => setProfForm((f) => ({ ...f, code: e.target.value }))}
                placeholder="es. fisioterapista"
                data-testid="prof-code-input"
              />
            </div>
            <div>
              <Label>Etichetta</Label>
              <Input
                value={profForm.label}
                onChange={(e) => setProfForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="es. Fisioterapista"
                data-testid="prof-label-input"
              />
            </div>
            <div>
              <Label>Ordine</Label>
              <Input
                type="number"
                value={profForm.order}
                onChange={(e) => setProfForm((f) => ({ ...f, order: Number(e.target.value) || 0 }))}
                data-testid="prof-order-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={!!profForm.active}
                onCheckedChange={(v) => setProfForm((f) => ({ ...f, active: v }))}
                data-testid="prof-active-switch"
              />
              <Label>Attiva</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfOpen(false)}>Annulla</Button>
            <Button onClick={saveProf} data-testid="save-prof-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service dialog */}
      <Dialog open={svcOpen} onOpenChange={setSvcOpen}>
        <DialogContent data-testid="service-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{svcEditingId ? 'Modifica prestazione' : 'Nuova prestazione'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome</Label>
              <Input
                value={svcForm.name}
                onChange={(e) => setSvcForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="es. Iniezione intramuscolare"
                data-testid="svc-name-input"
              />
            </div>
            <div>
              <Label>Professioni abilitate (multi-select)</Label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="svc-prof-chips">
                {professions.filter((p) => p.active).map((p) => {
                  const on = svcForm.profession_codes.includes(p.code);
                  return (
                    <button
                      key={p.code}
                      type="button"
                      onClick={() => toggleProfCode(p.code)}
                      className={`px-3 py-1 rounded-full border text-xs transition ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
                      data-testid={`svc-prof-chip-${p.code}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Costo in crediti</Label>
                <Input
                  type="number"
                  value={svcForm.credit_cost}
                  onChange={(e) => setSvcForm((f) => ({ ...f, credit_cost: e.target.value }))}
                  data-testid="svc-credit-input"
                />
              </div>
              <div>
                <Label>Prezzo default €</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={svcForm.default_price_eur}
                  onChange={(e) => setSvcForm((f) => ({ ...f, default_price_eur: e.target.value }))}
                  data-testid="svc-price-input"
                />
              </div>
            </div>
            <div>
              <Label>Descrizione (opzionale)</Label>
              <Textarea
                rows={2}
                value={svcForm.description}
                onChange={(e) => setSvcForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="svc-desc-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={!!svcForm.active}
                onCheckedChange={(v) => setSvcForm((f) => ({ ...f, active: v }))}
                data-testid="svc-active-switch"
              />
              <Label>Attiva</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSvcOpen(false)}>Annulla</Button>
            <Button onClick={saveSvc} data-testid="save-svc-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxonomyPage;
